import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import Modal from '../../../common/components/organisms/Modal';
// Removed unused FlexContainer import
import { getAnnotationColor, getAnnotationIcon } from '../utils/annotationPresentation';
import TaskAnnotationModal from './TaskAnnotationModal';
import NoteAnnotationModal from './NoteAnnotationModal';
import RepairAnnotationModal from './RepairAnnotationModal';
import AlarmAnnotationModal from './AlarmAnnotationModal';
import {
  ModalContent,
  HeaderSection,
  IconCircle,
  HeaderText,
  Title,
  Subtitle,
  ContentSection,
  SectionTitle,
  Description,
  MetadataGrid,
  MetadataItem,
  MetadataLabel,
  MetadataValue,
  TaskSpecificSection,
  TaskField,
  TaskLabel,
  TaskValue,
} from './AnnotationModal.styled';

function formatAnnotationType(type) {
  return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

function AnnotationModal(props) {
  const { t } = useTranslation();
  const { annotation, onClose } = props;

  if (annotation && annotation.type === 'task') {
    return <TaskAnnotationModal annotation={annotation} onClose={onClose} />;
  }
  if (annotation && annotation.type === 'note') {
    return <NoteAnnotationModal annotation={annotation} onClose={onClose} />;
  }
  if (annotation && annotation.type === 'repair') {
    return <RepairAnnotationModal annotation={annotation} onClose={onClose} />;
  }
  if (annotation && annotation.type === 'alarm') {
    return <AlarmAnnotationModal annotation={annotation} onClose={onClose} />;
  }

  const typeColor = getAnnotationColor(annotation, { respectSeverity: false });
  const typeIcon = getAnnotationIcon(annotation.type);
  const isTask = annotation.type === 'task';
  const baseMetadata = annotation.base_metadata || {};

  return (
    <Modal
      title={t('annotations.modal.detailsTitle', { type: formatAnnotationType(annotation.type) })}
      close={onClose}
      footer={() => (
        <button onClick={onClose}>{t('common.close')}</button>
      )}
    >
      <ModalContent>
        <HeaderSection>
          <IconCircle bg={typeColor}>
            <span aria-hidden="true">{typeIcon}</span>
          </IconCircle>
          <HeaderText>
            <Title>{formatAnnotationType(annotation.type)}</Title>
            <Subtitle>
              {t('annotations.modal.createdBy', {
                timestamp: moment(annotation.created_at).format('MMM DD, YYYY HH:mm'),
                user: annotation.created_by_name || annotation.created_by
              })}
            </Subtitle>
          </HeaderText>
        </HeaderSection>

        <ContentSection>
          <SectionTitle>{t('annotations.modal.sections.description')}</SectionTitle>
          <Description>
            {annotation.description || t('annotations.modal.noDescription')}
          </Description>
        </ContentSection>

        <ContentSection>
          <SectionTitle>{t('annotations.modal.sections.details')}</SectionTitle>
          <MetadataGrid>
            <MetadataItem>
              <MetadataLabel>{t('annotations.modal.fields.type')}</MetadataLabel>
              <MetadataValue>{formatAnnotationType(annotation.type)}</MetadataValue>
            </MetadataItem>
            <MetadataItem>
              <MetadataLabel>{t('annotations.modal.fields.severity')}</MetadataLabel>
              <MetadataValue>{annotation.severity || t('common.na')}</MetadataValue>
            </MetadataItem>
            <MetadataItem>
              <MetadataLabel>{t('annotations.modal.fields.startTime')}</MetadataLabel>
              <MetadataValue>{moment(annotation.start_time).format('MMM DD, YYYY HH:mm')}</MetadataValue>
            </MetadataItem>
            {annotation.end_time && (
              <MetadataItem>
                <MetadataLabel>{t('annotations.modal.fields.endTime')}</MetadataLabel>
                <MetadataValue>{moment(annotation.end_time).format('MMM DD, YYYY HH:mm')}</MetadataValue>
              </MetadataItem>
            )}
            <MetadataItem>
              <MetadataLabel>{t('annotations.modal.fields.sourceType')}</MetadataLabel>
              <MetadataValue>{annotation.source_type || t('common.na')}</MetadataValue>
            </MetadataItem>
            <MetadataItem>
              <MetadataLabel>{t('annotations.modal.fields.sourceId')}</MetadataLabel>
              <MetadataValue>{annotation.source_id || t('common.na')}</MetadataValue>
            </MetadataItem>
          </MetadataGrid>
        </ContentSection>

        {isTask && (
          <TaskSpecificSection>
            <SectionTitle>{t('annotations.modal.sections.taskInformation')}</SectionTitle>

            {baseMetadata.task_name && (
              <TaskField>
                <TaskLabel>{t('annotations.modal.taskFields.taskName')}</TaskLabel>
                <TaskValue>{baseMetadata.task_name}</TaskValue>
              </TaskField>
            )}

            {baseMetadata.task_type && (
              <TaskField>
                <TaskLabel>{t('annotations.modal.taskFields.taskType')}</TaskLabel>
                <TaskValue>{baseMetadata.task_type}</TaskValue>
              </TaskField>
            )}

            {baseMetadata.condition && (
              <TaskField>
                <TaskLabel>{t('annotations.modal.taskFields.condition')}</TaskLabel>
                <TaskValue>{baseMetadata.condition}</TaskValue>
              </TaskField>
            )}

            {baseMetadata.recommended_action && (
              <TaskField>
                <TaskLabel>{t('annotations.modal.taskFields.recommendedAction')}</TaskLabel>
                <TaskValue>{baseMetadata.recommended_action}</TaskValue>
              </TaskField>
            )}

            {baseMetadata.user_role_assigned && (
              <TaskField>
                <TaskLabel>{t('annotations.modal.taskFields.assignedRole')}</TaskLabel>
                <TaskValue>{baseMetadata.user_role_assigned}</TaskValue>
              </TaskField>
            )}

            {baseMetadata.state && (
              <TaskField>
                <TaskLabel>{t('annotations.modal.taskFields.state')}</TaskLabel>
                <TaskValue>{baseMetadata.state}</TaskValue>
              </TaskField>
            )}
          </TaskSpecificSection>
        )}
      </ModalContent>
    </Modal>
  );
}

AnnotationModal.propTypes = {
  annotation: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    type: PropTypes.string.isRequired,
    start_time: PropTypes.string.isRequired,
    end_time: PropTypes.string,
    description: PropTypes.string,
    severity: PropTypes.oneOf(['low', 'medium', 'high', 'critical']),
    created_by: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    created_by_name: PropTypes.string,
    created_at: PropTypes.string,
    source_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    source_type: PropTypes.string,
    base_metadata: PropTypes.object,
    user_metadata: PropTypes.object
  }).isRequired,
  onClose: PropTypes.func.isRequired
};

export default AnnotationModal;
